// CacheEntry — the per-query record `QueryClient` tracks in its cache map.
// Split out of index.tsx (#217 item 3) when the invalidation-while-fetching
// mark pushed the file over the 600-line lint budget; the entry shape plus
// its factory are a self-contained unit (same split pattern as polling.ts).
import type { QueryFunctionContext } from "./index";

export type Subscriber = () => void;
export type Status = "idle" | "loading" | "success" | "error";

export interface CacheEntry {
  data: unknown;
  error: unknown;
  status: Status;
  fetchedAt: number;
  writeSeq: number;
  promise: Promise<unknown> | null;
  /** #217 item 3: an invalidation that lands while a fetch is in flight must
   *  not be swallowed — the in-flight response predates the invalidation
   *  (e.g. trade settle #2 arriving while settle #1's history refetch is
   *  airborne). When set, the resolving fetch keeps the entry stale so the
   *  subscriber effect immediately refetches. */
  invalidatedWhileFetching: boolean;
  abortController: AbortController | null;
  subscribers: Set<Subscriber>;
  refetchTimer: ReturnType<typeof setInterval> | null;
  refetchIntervalMs: number | null;
  refetchIntervals: Map<number, number>;
  refetchQueryFn: ((context: QueryFunctionContext) => Promise<unknown>) | null;
}

/** Fresh idle entry — every field starts at its "never fetched" zero value.
 *
 * @example
 *   const entry = newEntry();
 *   cache.set(serializeKey(queryKey), entry);
 */
export function newEntry(): CacheEntry {
  return {
    data: undefined,
    error: null,
    status: "idle",
    fetchedAt: 0,
    writeSeq: 0,
    promise: null,
    invalidatedWhileFetching: false,
    abortController: null,
    subscribers: new Set(),
    refetchTimer: null,
    refetchIntervalMs: null,
    refetchIntervals: new Map(),
    refetchQueryFn: null,
  };
}
